<template>
  <v-container fluid class="pa-0">
    <!-- ── Header ── -->
    <div class="d-flex flex-wrap justify-space-between align-center mb-5 ga-3">
      <div>
        <div class="page-title">Monitor Students</div>
        <div class="page-subtitle">
          Track student requirement submissions and progress
        </div>
      </div>
      <div class="d-flex align-center ga-3">
        <v-text-field
          v-model="search"
          placeholder="Search student..."
          prepend-inner-icon="mdi-magnify"
          variant="outlined"
          density="compact"
          hide-details
          clearable
          style="width: 230px"
          color="primary"
        />
        <v-autocomplete
          density="compact"
          v-model="batch"
          item-value="id"
          item-title="name"
          @update:modelValue="fetchStudents"
          :color="$vuetify.theme.themes.light.inputColor"
          variant="outlined"
          hide-details
          :items="batch_list"
        />
        <v-select
          v-model="programFilter"
          :items="programOptions"
          variant="outlined"
          density="compact"
          hide-details
          style="width: 185px"
        />
      </div>
    </div>

    <!-- ── Table card ── -->
    <div class="monitor-card">
      <!-- Column headers -->
      <div class="monitor-thead">
        <div class="col-student">Student Info</div>
        <div class="col-program">Program &amp; Section</div>
        <div class="col-deploy">Deployment</div>
        <div class="col-progress">Requirement Progress</div>
        <div class="col-actions">Actions</div>
      </div>

      <v-divider />

      <!-- Loading -->
      <div v-if="loading" class="py-4 px-4">
        <v-skeleton-loader
          v-for="n in 5"
          :key="n"
          type="list-item-two-line"
          class="mb-2"
        />
      </div>

      <!-- Empty -->
      <div v-else-if="!filteredData.length" class="monitor-empty">
        <v-icon size="36" color="#cbd5e1">mdi-account-off-outline</v-icon>
        <div class="monitor-empty-text">No students found.</div>
      </div>

      <!-- Rows -->
      <template v-else>
        <div v-for="(item, idx) in filteredData" :key="item.id">
          <div class="monitor-row">
            <!-- Student Info -->
            <div class="col-student">
              <div class="student-name">{{ item.name }}</div>
              <div class="student-sid">{{ item.school_id }}</div>
            </div>

            <!-- Program & Section -->
            <div class="col-program">
              <span class="program-text">{{
                item.program_abbr || item.program
              }}</span>
            </div>

            <!-- Deployment -->
            <div class="col-deploy">
              <span
                class="status-pill"
                :style="statusStyle(item.internship_status)"
                >{{ statusLabel(item.internship_status) }}</span
              >
            </div>

            <!-- Requirement Progress -->
            <div class="col-progress">
              <div class="progress-top">
                <span class="progress-label">
                  {{ getProgress(item).submitted }}/{{
                    getProgress(item).total
                  }}
                  Completed
                </span>
                <span class="progress-pct">{{ getProgressPct(item) }}%</span>
              </div>
              <div class="progress-track">
                <div
                  class="progress-fill"
                  :style="{
                    width: getProgressPct(item) + '%',
                    backgroundColor: getProgressBarColor(getProgressPct(item)),
                  }"
                ></div>
              </div>
            </div>

            <!-- Actions -->
            <div class="col-actions">
              <v-btn
                variant="text"
                size="small"
                color="#64748b"
                prepend-icon="mdi-eye-outline"
                class="view-btn"
                @click="viewDetails(item)"
              >
                View Details
              </v-btn>
            </div>
          </div>

          <v-divider v-if="idx < filteredData.length - 1" />
        </div>
      </template>
    </div>
  </v-container>
</template>

<script>
export default {
  name: "MonitorStudentsDataTable",

  data: () => ({
    search: "",
    programFilter: "All Programs",
    data: [],
    loading: false,
    batch: null,
    batch_list: [],
  }),

  computed: {
    programOptions() {
      const names = [
        ...new Set(
          this.data.map((d) => d.program_abbr || d.program).filter(Boolean),
        ),
      ];
      return ["All Programs", ...names.sort()];
    },

    filteredData() {
      let result = this.data;

      if (this.programFilter && this.programFilter !== "All Programs") {
        result = result.filter(
          (d) => (d.program_abbr || d.program) === this.programFilter,
        );
      }

      if (this.search) {
        const q = this.search.toLowerCase();
        result = result.filter(
          (d) =>
            (d.name || "").toLowerCase().includes(q) ||
            (d.school_id || "").toLowerCase().includes(q),
        );
      }

      return result;
    },
  },

  mounted() {
    this.loadBatchThenFetch();
  },

  methods: {
    loadBatchThenFetch() {
      this.axiosCall("/program/getProgramsBatchByInstitute", "GET").then(
        (res) => {
          if (res && res.data && res.data.length) {
            console.log("BATCH Load", res.data);
            this.batch_list = res.data;
            this.batch = res.data[0].id;
            this.fetchStudents();
          }
        },
      );
    },

    fetchStudents() {
      if (!this.batch) return;
      this.loading = true;
      this.axiosCall("/student/getDeanView/" + this.batch, "GET")
        .then((res) => {
          if (res && res.data) {
            this.data = res.data.map((s) => ({
              ...s,
              name: this.toTitleCase(s.name),
            }));
          }
        })
        .finally(() => {
          this.loading = false;
        });
    },

    getProgress(item) {
      const reqs = item.requirements || [];
      return {
        submitted: reqs.filter((r) => r.status == 2).length,
        total: reqs.length,
      };
    },

    getProgressPct(item) {
      const { submitted, total } = this.getProgress(item);
      if (!total) return 0;
      return Math.round((submitted / total) * 100);
    },

    getProgressBarColor(pct) {
      if (pct >= 75) return "#22c55e";
      if (pct >= 50) return "#f59e0b";
      return "#ef4444";
    },

    statusLabel(status) {
      const map = {
        1: "Searching",
        2: "Searching",
        3: "Deployed",
        4: "Completed",
        5: "Pulled Out",
        6: "Terminated",
      };
      return map[status] || "For Matching";
    },

    statusStyle(status) {
      const map = {
        1: { backgroundColor: "#fef9c3", color: "#b45309" },
        2: { backgroundColor: "#fef9c3", color: "#b45309" },
        3: { backgroundColor: "#dcfce7", color: "#16a34a" },
        4: { backgroundColor: "#d1fae5", color: "#059669" },
        5: { backgroundColor: "#ffedd5", color: "#c2410c" },
        6: { backgroundColor: "#fee2e2", color: "#dc2626" },
      };
      return map[status] || { backgroundColor: "#f1f5f9", color: "#374151" };
    },

    viewDetails(item) {
      const typePathMap = {
        3: "ojt-coordinator",
        4: "instructor",
        5: "dean",
        6: "vpaa",
        10: "program-chair",
      };
      const userTypeId = this.$store.state.user?.user?.usertypeID;
      const basePath = typePathMap[userTypeId] || "program-chair";
      sessionStorage.setItem("monitorStudent", JSON.stringify(item));
      this.$router.push(`/${basePath}/monitor-students/${item.id}`);
    },
  },
};
</script>

<style scoped>
/* ── Page header ── */
.page-title {
  font-size: 26px;
  font-weight: 800;
  color: #111827;
  letter-spacing: -0.5px;
}
.page-subtitle {
  font-size: 13px;
  color: #64748b;
  margin-top: 2px;
}

/* ── Table card ── */
.monitor-card {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  box-shadow: 0 1px 6px rgba(0, 0, 0, 0.06);
  overflow: hidden;
}

/* ── Header row ── */
.monitor-thead {
  display: flex;
  align-items: center;
  padding: 14px 24px;
  background: #f9fafb;
  font-size: 12px;
  font-weight: 700;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* ── Data row ── */
.monitor-row {
  display: flex;
  align-items: center;
  padding: 16px 24px;
  transition: background 0.12s;
}
.monitor-row:hover {
  background: #f8fafc;
}

/* ── Column widths (must match header) ── */
.col-student {
  flex: 3;
  min-width: 0;
}
.col-program {
  flex: 2;
  min-width: 0;
}
.col-deploy {
  flex: 2;
  min-width: 0;
}
.col-progress {
  flex: 4;
  min-width: 0;
  padding-right: 16px;
}
.col-actions {
  flex: 2;
  min-width: 0;
  display: flex;
  justify-content: flex-end;
}

/* ── Student info ── */
.student-name {
  font-size: 14px;
  font-weight: 700;
  color: #111827;
  line-height: 1.3;
}
.student-sid {
  font-size: 12px;
  color: #94a3b8;
  margin-top: 2px;
}

/* ── Program ── */
.program-text {
  font-size: 13.5px;
  font-weight: 600;
  color: #374151;
}

/* ── Status pill ── */
.status-pill {
  display: inline-block;
  padding: 4px 14px;
  border-radius: 99px;
  font-size: 13px;
  font-weight: 600;
  line-height: 1.4;
  white-space: nowrap;
}

/* ── Progress ── */
.progress-top {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 6px;
}
.progress-label {
  font-size: 12.5px;
  font-weight: 600;
  color: #374151;
}
.progress-pct {
  font-size: 12px;
  font-weight: 700;
  color: #6b7280;
}
.progress-track {
  height: 8px;
  background: #e5e7eb;
  border-radius: 99px;
  overflow: hidden;
}
.progress-fill {
  height: 100%;
  border-radius: 99px;
  transition: width 0.4s ease;
}

/* ── View Details ── */
.view-btn {
  font-size: 13px;
  font-weight: 600;
  text-transform: none;
  letter-spacing: 0;
}

/* ── Empty ── */
.monitor-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 48px 0;
  gap: 10px;
}
.monitor-empty-text {
  font-size: 13px;
  color: #94a3b8;
}
</style>
